define((require) => {
    "use strict";

    const $                  = require("jquery");
    const _                  = require("underscore");
    const AbstractWidgetView = require("shared/ui/experience/widgets/AbstractWidgetView");
    const Browser            = require("shared/services/Browser");
    const YouTubeIframeAPI   = require("shared/services/YouTubeIframeAPI");
    const Promise            = require("shared/util/Promise");

    /**
     * Method for creating the youtube element.
     *
     * @param  {YT} YT  The <tt>YT</tt> iframe API, loaded via the <tt>YouTubeIframeAPI</tt>.
     * @param  {number} width The width of the video element.
     * @param  {number} height The height of the video element.
     * @param  {object} vars Object containing all of the player variables.
     * @param  {string} videoID String containing specific id for the video.
     * @param  {HTMLElement} element The raw <tt>HTMLElement</tt>.
     *
     * @return {Promise}        <tt>Promise</tt> that resolves when the player instance is considered *ready*.
     */
    function youTubePlayerReady(YT, width, height, vars, videoID, element) {
        return Promise.deferred((deferred) => {
            const player = new YT.Player(element, {
                playerVars: vars,
                height,
                width,
                videoId: videoID,
                events: {
                    onReady() {
                        deferred.resolve(player);
                    }
                }
            });
        }).promise();
    }

    /**
     * Create the container around the preview image
     *
     * @param {object} bounds bounds of widget. Must contain a <code>width</code> and <code>height</code> key
     * @return {jQueryDomObject} jQuery html element
     */
    const createYoutubePreviewImageContainer = ({width, height}) => (
        $(`<div class="youtube-preview-video-image-container" style="width: ${width}px; height: ${height}px" />`)
    );

    /**
     * The view rendering an YouTube widget.
     *
     * @class YouTubeWidgetView
     * @constructor
     *
     * @cfg {YouTubeWidgetModel} model The model of the Youtube widget being rendered.
     */
    return AbstractWidgetView.extend("YouTubeWidget", {

        tagName: "div",

        className: `${AbstractWidgetView.prototype.className} youtube-widget`,

        _inactivePane: null,

        _videoElement: null,

        _activeState: false,

        _player: null,

        /**
         * Creates the YouTube player instance by loading the API and replacing the element with the iframe -
         * returning a promise that resolves with the new player instance.<p>
         *
         * @param  {jquery} element A jquery wrapped <tt>HTMLElement</tt>.
         * @return {Promise} <tt>Promise</tt> that resolves when the player instance is considered *ready*.
         */
        _createPlayer(element) {
            const videoId = this.model.get("videoID");
            const playerEl = element.get(0);
            const vars = this.model.getPlayerVars();
            const width = this._bounds.width;
            const height = this._bounds.height;

            return YouTubeIframeAPI.load().then((YT) => youTubePlayerReady(YT, width, height, vars, videoId, playerEl));
        },

        /**
         * Handles play/pause state - invoked when the active state changes.
         */
        _updateToActiveState() {
            // Get the youtube Iframe API since we need more than what is in the player
            YouTubeIframeAPI.load().then((YT) => {
                // See https://developers.google.com/youtube/iframe_api_reference
                if (this._player) {
                    if (this._activeState && this.model.get("autoplay")) {
                        // Handles special case when when player has ended and still need to be started
                        if (this._player.getPlayerState() === YT.PlayerState.ENDED) {
                            this._player.seekTo(0, true);
                        }

                        // Since most mobile and tablet devices do not support auto play
                        // we want to only call playVideo for desktop browsers to avoid potential
                        // errors or endless loading spinners (see MOS-3197)
                        if (Browser.environment.desktop) {
                            this._player.playVideo();
                        }
                    } else if (this._player.getPlayerState() === YT.PlayerState.PLAYING) {
                        // Only pause the player if it actually is playing
                        this._player.pauseVideo();
                    }
                }
            });
        },

        updateRendering(element, bounds, active, edited, changedProperties) {
            this._bounds = bounds;

            // Determine whether the active state has changed since the last update
            const activeChanged = active !== this._activeState;

            // Minor hack to handle the thumbnail state
            const staticRepresentation = !this.options.enableAnimations;
            const videoID = this.model.get("videoID");
            const isThumbnail = !active && !edited && staticRepresentation && videoID;
            const isInSceneInEditor = !active && edited;
            const videoPreviewImageUrl = `//img.youtube.com/vi/${videoID}/hqdefault.jpg`;

            this._activeState = active;

            // Render the iframe if it hasn't already been rendered, or if certain properties have changed (when editing)
            if (!this._videoElement || (changedProperties && !_.isUndefined(changedProperties.videoID))) {
                element.empty();

                if (isInSceneInEditor) {
                    // Create a preview image of the youtube preview if we have a video ID.

                    this._inactivePane = createYoutubePreviewImageContainer(bounds).appendTo(element);

                    if (videoID) {
                        this._inactivePane.css("background-image", `url("${videoPreviewImageUrl}")`);
                        this._inactivePane.append(`<div class="youtube-play-button-overlay" />`);
                    }

                // This covers the thumbnail state
                } else if (isThumbnail) {
                    // Instead of constructing an iframe element, we create an image element
                    // displaying the associated poster image
                    this._inactivePane = createYoutubePreviewImageContainer(bounds).appendTo(element);
                    this._inactivePane.css("background-image", `url("${videoPreviewImageUrl}")`);
                } else {
                    // We're in the viewer...
                    this._createElement(element).then((player) => {
                        this._player = player;
                        this._videoElement = $(this._player.getIframe());

                        // Mute Initially...
                        if (this.model.get("mute")) {
                            this._player.mute();
                        }

                        // Update state...
                        this._updateToActiveState();
                    }).fail(() => {
                        this._inactivePane = createYoutubePreviewImageContainer(bounds).appendTo(element);
                    });
                }

                // add no-video-id class if no video id, otherwise remove it
                element.toggleClass("no-video-id", !videoID);

                return;
            }

            // Ensure the iframe has the correct size if the properties might have changed
            if (changedProperties) {
                const elementWidth = element.css("width");
                const elementHeight = element.css("height");

                this._setElementSize(this._videoElement, elementWidth, elementHeight);
                this._setElementSize(this._inactivePane, elementWidth, elementHeight);
            }

            // Reload the iframe if it has gone from active to inactive, to stop the video from playing while hidden, or
            // if it has gone from inactive to active and has autoplay enabled

            if (activeChanged) {
                this._updateToActiveState();
            }
        },

        _createElement(parentElement) {
            if (this.model.has("videoID")) {
                this._videoElement = $("<div />").appendTo(parentElement);

                return this._createPlayer(this._videoElement);
            }
            return Promise.rejected("No videoID");
        },

        _setElementSize(element, width, height) {
            if (element) {
                element.css({width, height});
            }
        },

        onClose() {
            this.__super();

            // Dispose player...?
            if (this._player) {
                this._player.clearVideo();
            }
        }
    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/youtubeWidget/YouTubeWidgetView.js